import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import api from "../lib/api";

const LAST_UPDATED = "5 August 2026";

const PageShell = ({ title, intro, children }) => (
  <div className="min-h-screen bg-[#050506] pt-32 pb-24">
    <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-4xl md:text-5xl font-bold text-white mb-4"
      >
        {title}
      </motion.h1>
      <p className="text-sm text-gray-500 mb-4">Last updated: {LAST_UPDATED}</p>
      {intro && <p className="text-lg text-gray-300 leading-relaxed mb-10">{intro}</p>}
      <div className="space-y-8 text-gray-400 leading-relaxed">{children}</div>
    </main>
  </div>
);

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="text-xl md:text-2xl font-semibold text-white">{title}</h2>
    <div className="space-y-3">{children}</div>
  </section>
);

const LegalIdentity = () => (
  <section className="p-6 bg-white/[0.03] border border-white/10 rounded-xl">
    <h2 className="text-xl font-semibold text-white mb-3">Business identity</h2>
    <p>OrvDigital is owned and operated by Nitesh Kumar Vishwakarma, a sole proprietorship based in India.</p>
    <p className="mt-3">
      For support, complaints, or legal requests, please use our{" "}
      <Link to="/contact" className="text-purple-400 hover:text-purple-300 underline underline-offset-4">
        Contact page
      </Link>.
    </p>
  </section>
);

const BulletList = ({ children }) => (
  <ul className="list-disc pl-6 space-y-2">{children}</ul>
);

export const PrivacyPage = () => (
  <PageShell
    title="Privacy Policy"
    intro="This Privacy Policy explains how OrvDigital collects, uses, stores, and shares personal information when you visit our website, create an account, contact us, or purchase digital products."
  >
    <LegalIdentity />
    <Section title="Information we collect">
      <BulletList>
        <li>Account information, such as your name, email address, login credentials, and verification status.</li>
        <li>Order and transaction information, including products purchased, amounts, payment status, invoices, refunds, and license records.</li>
        <li>Support information that you provide through forms, email, or other communications.</li>
        <li>Technical and usage information, such as IP address, browser type, device information, timestamps, pages visited, cookies, and security logs.</li>
      </BulletList>
      <p>We do not intentionally store full card, bank-account, or UPI credentials. Payments are handled by an approved third-party payment provider under its own terms and privacy practices.</p>
    </Section>
    <Section title="How we use information">
      <BulletList>
        <li>To create and secure accounts, verify users, and provide customer support.</li>
        <li>To process orders, confirm verified payments, deliver digital products, issue licenses, and manage refunds or disputes.</li>
        <li>To send transactional messages such as verification codes, purchase confirmations, license details, and important service notices.</li>
        <li>To prevent fraud, abuse, unauthorized access, and violations of our terms or licenses.</li>
        <li>To improve the website, understand service performance, maintain records, and comply with legal obligations.</li>
      </BulletList>
    </Section>
    <Section title="Legal basis and consent">
      <p>We process personal information for purposes connected with providing the service you request, fulfilling purchases, complying with law, protecting the platform, and other lawful purposes. Where consent is required, you may withdraw it by contacting us, subject to information we must retain for legal, security, or transaction-record purposes.</p>
    </Section>
    <Section title="Sharing of information">
      <p>We may share only the information reasonably necessary with service providers that support payments, hosting, email delivery, analytics, security, customer support, accounting, or legal compliance. We may also disclose information when required by law, to protect rights and safety, or in connection with a lawful business transfer.</p>
      <p>We do not sell personal information.</p>
    </Section>
    <Section title="Cookies and local storage">
      <p>We may use essential cookies or browser storage for login sessions, security, preferences, and core website operation. Analytics technologies may be used to understand aggregate usage. You can manage non-essential cookies through your browser, although blocking essential storage may affect the service.</p>
    </Section>
    <Section title="Retention and security">
      <p>We retain information only as long as reasonably necessary for the purposes described above, including order records, licenses, fraud prevention, dispute handling, and legal obligations. We use reasonable technical and organizational safeguards, but no online system can guarantee absolute security.</p>
    </Section>
    <Section title="Your choices and rights">
      <p>You may request access to, correction of, or deletion of eligible personal information, withdraw consent where applicable, or raise a grievance through our Contact page. We may need to verify your identity before acting on a request. Certain transaction or security records may be retained where legally required or reasonably necessary.</p>
    </Section>
    <Section title="Children">
      <p>Our services are not directed to children. A person who is not legally capable of entering a binding contract should use the service only with the involvement and consent of a parent or lawful guardian.</p>
    </Section>
    <Section title="Policy changes">
      <p>We may update this policy when our practices, services, or legal obligations change. The revised version will be posted here with a new “Last updated” date.</p>
    </Section>
  </PageShell>
);

export const TermsPage = () => (
  <PageShell
    title="Terms & Conditions"
    intro="These Terms & Conditions govern your access to OrvDigital and your purchase or use of digital products, licenses, and related services."
  >
    <LegalIdentity />
    <Section title="Acceptance and eligibility">
      <p>By using the website or placing an order, you agree to these Terms, our Privacy Policy, Refund Policy, and the license displayed for the relevant product. You must provide accurate information and be legally capable of entering a binding contract.</p>
    </Section>
    <Section title="Accounts and security">
      <p>You are responsible for maintaining the confidentiality of your login credentials and for activity under your account. Notify us promptly of suspected unauthorized use. We may restrict or suspend access when reasonably necessary to protect users, investigate abuse, or enforce these Terms.</p>
    </Section>
    <Section title="Products, pricing, and orders">
      <p>Product descriptions, included files, applicable licenses, prices, taxes, and delivery details are shown on the product page or during checkout. Prices may change prospectively. An order is accepted only after payment is successfully verified by our payment system. We may cancel or refund an order affected by pricing errors, fraud indicators, technical problems, legal restrictions, or product unavailability.</p>
    </Section>
    <Section title="Payments and digital delivery">
      <p>Payments are processed by an approved third-party payment provider. Digital access, downloads, or license credentials are issued only after verified payment. Delivery may occur through the customer dashboard, email, a secure link, or another method stated on the product page. You are responsible for providing a working email address and maintaining access to your account.</p>
    </Section>
    <Section title="Permitted use and licenses">
      <p>Your purchase grants only the rights expressly stated in the applicable product license. Ownership of the underlying intellectual property is not transferred. You must not share account access, distribute source files, resell a Creator License, remove ownership notices, use content unlawfully, or claim exclusive ownership unless the applicable license expressly permits that action.</p>
    </Section>
    <Section title="Third-party and submitted content">
      <p>Some products may include or depend on third-party tools, formats, platforms, or licensed materials. Their separate terms may apply. Partners and sellers must have all rights required for content they submit and remain responsible for its accuracy and legality.</p>
    </Section>
    <Section title="Prohibited conduct">
      <BulletList>
        <li>Attempting unauthorized access, scraping protected content, bypassing security, or interfering with the service.</li>
        <li>Using the website or products for unlawful, deceptive, infringing, harmful, or fraudulent activity.</li>
        <li>Misusing payment disputes, chargebacks, promotional offers, licenses, or download access.</li>
        <li>Uploading malware or content that violates another person’s rights.</li>
      </BulletList>
    </Section>
    <Section title="Refunds and payment disputes">
      <p>Refund requests are handled under our Refund Policy and applicable law. Contact us first if you experience duplicate payment, missing delivery, corrupted files, or unauthorized transactions so we can investigate. Nothing in these Terms limits rights that cannot lawfully be excluded.</p>
    </Section>
    <Section title="Service availability and warranties">
      <p>We aim to keep the service accurate and available, but maintenance, third-party failures, updates, or events outside our reasonable control may cause interruptions. Except for obligations that cannot lawfully be excluded, products and services are provided on an “as available” basis without guarantees of specific commercial, audience, income, or performance results.</p>
    </Section>
    <Section title="Liability">
      <p>To the extent permitted by law, OrvDigital is not responsible for indirect or consequential loss, lost profits, or loss caused by misuse, incompatible third-party software, or matters outside our reasonable control. Any limitation applies only to the extent lawful and does not affect mandatory consumer rights.</p>
    </Section>
    <Section title="Governing law and complaints">
      <p>These Terms are governed by the laws of India. Before starting formal proceedings, please contact us with the relevant account email, order reference, and a clear description of the issue so we can attempt a fair resolution. Statutory consumer remedies and competent legal forums remain available where applicable.</p>
    </Section>
    <Section title="Changes">
      <p>We may update these Terms prospectively. Material changes will be posted on this page with a revised date. Terms applicable to a completed purchase will not be changed retroactively except where required by law or agreed with you.</p>
    </Section>
  </PageShell>
);

export const RefundPage = () => (
  <PageShell
    title="Refund Policy"
    intro="This policy explains when refunds may be available for digital products purchased from OrvDigital."
  >
    <LegalIdentity />
    <Section title="Digital-product purchases">
      <p>Because digital products can be delivered or accessed immediately, a change of mind generally does not qualify for a refund after files, links, credentials, or license access have been delivered. This rule does not limit any non-waivable rights available under applicable law.</p>
    </Section>
    <Section title="When you may request a refund">
      <BulletList>
        <li>You were charged more than once for the same order.</li>
        <li>Payment was completed but the purchased product was not delivered or made accessible within a reasonable time after you contacted support.</li>
        <li>The delivered files are materially corrupted, incomplete, or substantially different from the product description, and we cannot provide a working replacement.</li>
        <li>The transaction was unauthorized, subject to reasonable verification and the payment provider’s process.</li>
        <li>A refund is otherwise required by applicable law.</li>
      </BulletList>
    </Section>
    <Section title="Requests that are generally ineligible">
      <BulletList>
        <li>Change of mind after delivery or access.</li>
        <li>Failure to read the product description, compatibility requirements, or license terms.</li>
        <li>Lack of the software, skills, device, storage, or internet connection needed to use the product.</li>
        <li>Dissatisfaction based only on expected followers, views, sales, income, or other results that were not guaranteed.</li>
        <li>Violation of the applicable license or evidence of completed download, copying, redistribution, or misuse, unless law requires otherwise.</li>
      </BulletList>
    </Section>
    <Section title="How to request a refund">
      <p>Use our Contact page and provide your account email, order or payment reference, product name, reason for the request, and relevant screenshots or evidence. Please submit the request promptly after discovering the issue. We may request additional information needed to verify and investigate it.</p>
    </Section>
    <Section title="Review and processing">
      <p>We review requests individually and may first offer technical support, replacement files, restoration of access, or correction of the order. If approved, the refund will normally be sent to the original payment method. Bank and payment-provider processing times are outside our control. Access and licenses connected with a refunded order may be revoked.</p>
    </Section>
    <Section title="Cancellations and failed payments">
      <p>An order that has not been paid or verified may be cancelled without digital delivery. A pending or failed payment is not treated as a completed purchase. If money is debited for a failed transaction, reversal timing is generally determined by the bank or payment provider; contact us if the amount is not reversed within the period communicated by them.</p>
    </Section>
  </PageShell>
);

export const LicenseTermsPage = () => (
  <PageShell
    title="Digital Product License Terms"
    intro="These terms explain the default rules for using digital products purchased from OrvDigital. The license shown on a product page or order record controls if it includes more specific terms."
  >
    <LegalIdentity />
    <Section title="License grant">
      <p>After verified payment, OrvDigital grants you a limited, non-exclusive, non-transferable, and revocable license to use the purchased product only within the scope stated for that product. The purchase is a license to use content, not a transfer of intellectual-property ownership.</p>
    </Section>
    <Section title="Creator License">
      <p>Unless the product page states otherwise, a Creator License allows one customer to use the product in their own projects and published content. You may edit or combine the material as reasonably needed for that use, but you may not sell, share, sublicense, upload, or distribute the source files or substantially unchanged content as a competing resource bundle.</p>
    </Section>
    <Section title="Reseller License">
      <p>A Reseller License applies only when explicitly displayed and purchased. Its product-specific terms define what may be resold, whether modification is required, permitted channels, and any pricing or branding restrictions. A Reseller License does not grant exclusive rights or permission to represent yourself as the original owner unless expressly stated.</p>
    </Section>
    <Section title="Restrictions">
      <BulletList>
        <li>Do not share download links, credentials, source libraries, or license keys with unauthorized persons.</li>
        <li>Do not use the product for unlawful, defamatory, deceptive, infringing, or harmful purposes.</li>
        <li>Do not register the content, brand assets, or substantially similar material as your exclusive trademark or copyright.</li>
        <li>Do not use a Creator License for resale or redistribution.</li>
      </BulletList>
    </Section>
    <Section title="Third-party rights">
      <p>Products may contain elements governed by separate third-party licenses or platform rules. Those restrictions continue to apply. You are responsible for checking whether your planned use complies with the applicable platform, advertising, music, personality, trademark, and other third-party requirements.</p>
    </Section>
    <Section title="Termination">
      <p>The license may be suspended or terminated for material breach, fraud, unauthorized redistribution, or a refunded or reversed purchase. Termination does not remove obligations or liabilities that arose before termination.</p>
    </Section>
  </PageShell>
);

export const PartnerTermsPage = () => (
  <PageShell
    title="Partner / Seller Terms"
    intro="These terms apply to people or businesses approved to list or sell digital products through OrvDigital."
  >
    <LegalIdentity />
    <Section title="Approval and account information">
      <p>Participation is subject to review and is not guaranteed. Partners must provide accurate identity, payment, tax, contact, and product information and keep it current. We may request evidence of ownership, licensing rights, or authorization.</p>
    </Section>
    <Section title="Content rights and standards">
      <p>You must own or hold sufficient rights to every submitted product, preview, brand element, and description. Products must not be illegal, deceptive, infringing, malicious, or misleading. You grant OrvDigital the limited rights needed to host, preview, market, deliver, and support approved listings.</p>
    </Section>
    <Section title="Listings, pricing, and orders">
      <p>Listings, prices, discounts, licenses, and availability are subject to platform controls and approval. Order status becomes final only after payment verification. OrvDigital may reject, suspend, or remove listings to address quality, security, legal, payment-provider, or policy concerns.</p>
    </Section>
    <Section title="Earnings, holds, and adjustments">
      <p>Partner earnings are calculated according to the rate and conditions shown in the partner dashboard or applicable agreement. Amounts may remain on hold while payments settle or disputes are reviewed. Refunds, reversals, chargebacks, taxes, fees, fraud, duplicate transactions, or policy violations may reduce or reverse earnings before payout.</p>
    </Section>
    <Section title="Support and refunds">
      <p>Partners must provide reasonable product information and assist with genuine delivery or quality issues. OrvDigital may issue refunds or replacements under the Refund Policy, applicable law, or payment-provider requirements and adjust related earnings.</p>
    </Section>
    <Section title="Taxes and compliance">
      <p>Partners are responsible for their own registrations, invoices, tax reporting, and legal compliance unless a written agreement states otherwise. Nothing in these terms creates employment, agency, partnership, or authority to bind OrvDigital.</p>
    </Section>
    <Section title="Suspension and termination">
      <p>We may suspend listings, hold affected funds, or terminate participation for fraud, infringement, unresolved complaints, security threats, unlawful conduct, or material breach. Where appropriate, we will provide notice and an opportunity to respond.</p>
    </Section>
  </PageShell>
);

export const DisclaimerPage = () => (
  <PageShell
    title="Disclaimer"
    intro="This page explains important limitations concerning OrvDigital’s website, digital products, previews, and educational information."
  >
    <LegalIdentity />
    <Section title="No guaranteed results">
      <p>Examples, previews, testimonials, strategies, and educational material are provided for general information. We do not guarantee followers, views, engagement, revenue, sales, business growth, platform approval, or any other particular result. Results depend on many factors outside our control.</p>
    </Section>
    <Section title="Your responsibility">
      <p>You are responsible for evaluating whether a product is suitable for your intended purpose, software, device, audience, industry, and jurisdiction. You are also responsible for how you edit, publish, advertise, or otherwise use purchased content.</p>
    </Section>
    <Section title="Third-party platforms and content">
      <p>References to third-party brands, platforms, software, payment providers, or websites do not imply endorsement. Their services, rules, algorithms, availability, and terms may change without notice. OrvDigital does not control those third parties.</p>
    </Section>
    <Section title="Intellectual-property and platform compliance">
      <p>A license from OrvDigital does not replace permissions that may be required for music, trademarks, identifiable persons, third-party assets, advertising claims, or regulated uses. Review the applicable product license and third-party requirements before publication.</p>
    </Section>
    <Section title="Availability and accuracy">
      <p>We make reasonable efforts to keep descriptions and information accurate, but content may contain errors or become outdated. We may correct information, update products, or discontinue availability. Mandatory legal rights and obligations are not excluded by this disclaimer.</p>
    </Section>
  </PageShell>
);

export const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", order_email: "", subject: "", message: "", reference: "" });
  const [supportEmail, setSupportEmail] = useState("");
  React.useEffect(() => {
    api.get("/settings").then((r) => setSupportEmail(r.data?.support_email || r.data?.contact_email || "")).catch(() => {});
  }, []);
  const field = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50";
  const submit = async (e) => {
    e.preventDefault();

    try {
        await api.post("/contact", form);

        toast.success("Message sent! Our team will get back to you.");

        setForm({
            name: "",
            email: "",
            order_email: "",
            subject: "",
            message: "",
            reference: "",
        });
    } catch (err) {
        toast.error("Failed to send message. Please try again.");
    }
};
  return (
    <div className="min-h-screen bg-[#050506] pt-32 pb-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-5xl font-bold text-white mb-4">
          How can we help?
        </motion.h1>
        <p className="text-gray-400 mb-10">Send us a message and our team will get back to you.</p>
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <div className="p-5 bg-white/5 border border-white/10 rounded-xl">
            <Mail className="w-5 h-5 text-purple-400 mb-2" />
            {supportEmail ? (
              <a href={`mailto:${supportEmail}`} className="text-sm text-gray-300 hover:text-white break-all">{supportEmail}</a>
            ) : (
              <p className="text-sm text-gray-400">Reach our team any time using the form below — we typically respond within 1–2 business days.</p>
            )}
          </div>
          <Link to="/faq" className="p-5 bg-white/5 border border-white/10 rounded-xl hover:border-purple-500/30 transition-all">
            <HelpCircle className="w-5 h-5 text-purple-400 mb-2" />
            <p className="text-sm text-gray-400">Browse frequently asked questions.</p>
          </Link>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <input required placeholder="Name" className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input required type="email" placeholder="Email" className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input type="email" placeholder="Order email (optional)" className={field} value={form.order_email} onChange={(e) => setForm({ ...form, order_email: e.target.value })} />
            <input placeholder="Order/Reference number (optional)" className={field} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          </div>
          <input required placeholder="Subject" className={field} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <textarea required rows={6} placeholder="Your message..." className={field} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-purple-500 text-white py-4 rounded-xl">Send Message</Button>
        </form>
      </div>
    </div>
  );
};
